// src/api/notifications/notificationPreferencesApi.ts
import { ENV } from '../../config/runtimeEnv';

export const API_BASE_URL = ENV.VITE_NOTIFICATION_BASE_URL;

import type {
	NotificationPreferences,
	NotificationPreferencesResponse,
	UpdateNotificationPreferencesRequest,
} from '../../types/communications/NotificationTypes';
import type { UserUuid } from '../../types/identity';
import { createServiceClient } from '../../lib/http/client';

const createApiInstance = () => {
	return createServiceClient({
		baseURL: API_BASE_URL,
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

/**
 * Fetch notification preferences for a user.
 * Returns defaults (push + email enabled, sms disabled) if no stored preferences exist.
 */
export const fetchNotificationPreferences = async (
	userId: UserUuid
): Promise<NotificationPreferencesResponse> => {
	try {
		const api = createApiInstance();
		const response = await api.get<NotificationPreferencesResponse>(
			'/notification-preferences',
			{ params: { user_id: userId } }
		);
		return response.data;
	} catch (error) {
		console.error('Error fetching notification preferences:', error);
		throw error;
	}
};

/**
 * Create or update notification preferences for a user.
 */
export const updateNotificationPreferences = async (
	prefs: UpdateNotificationPreferencesRequest
): Promise<{ message: string; data: NotificationPreferences }> => {
	try {
		const api = createApiInstance();
		const response = await api.put<{ message: string; data: NotificationPreferences }>(
			'/notification-preferences',
			prefs
		);
		return response.data;
	} catch (error) {
		console.error('Error updating notification preferences:', error);
		throw error;
	}
};

/**
 * Reset notification preferences to system defaults (deletes stored preferences).
 */
export const resetNotificationPreferences = async (
	userId: UserUuid
): Promise<{ message: string; data: NotificationPreferences }> => {
	try {
		const api = createApiInstance();
		const response = await api.delete<{ message: string; data: NotificationPreferences }>(
			'/notification-preferences/reset',
			{ data: { user_id: userId } }
		);
		return response.data;
	} catch (error) {
		console.error('Error resetting notification preferences:', error);
		throw error;
	}
};
